import React, { useState, useMemo } from 'react';
import { 
  Card, Typography, Alert, Space, Select, Row, Col, 
  Empty, Paragraph, Switch, Divider, Progress 
} from 'antd';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';

const { Option } = Select;
const { Text, Paragraph: AntParagraph } = Typography;

export const HybridAnalysis = ({ 
  reportData,
  proposedTimetables,
  selectedRoute,
  selectedTrip,
  setSelectedRoute,
  setSelectedTrip,
  selectedOTR,
  STATISTICAL_MODELS
}) => {
  const [showStopTimes, setShowStopTimes] = useState(true);
  const [showDistributions, setShowDistributions] = useState(true);

  // Get unique routes for filter dropdown
  const routes = [...new Set(reportData.parsedData.map(item => item.routeNo))].sort();

  // Process data for the selected trip
  const processData = useMemo(() => {
    if (!selectedRoute || !selectedTrip || selectedRoute === 'all' || selectedTrip === 'all') {
      return null;
    }

    // Process stop time data
    const stopTimeData = reportData.parsedData
      .filter(item => 
        item.routeNo === selectedRoute &&
        item.tripId === selectedTrip
      )
      .sort((a, b) => {
        const dateCompare = a.tripDate.localeCompare(b.tripDate);
        if (dateCompare !== 0) return dateCompare;
        
        const [dateA, timeA] = a.scheduledTime.split(' ');
        const [dateB, timeB] = b.scheduledTime.split(' ');
        const [hoursA, minutesA] = timeA.split(':');
        const [hoursB, minutesB] = timeB.split(':');
        return (parseInt(hoursA) * 60 + parseInt(minutesA)) - (parseInt(hoursB) * 60 + parseInt(minutesB));
      });

    console.log('Stop Time Data:', stopTimeData.map(item => ({
      stopId: item.stopId,
      stopName: item.stopName,
      scheduledTime: item.scheduledTime
    })));

    // Group stop time data by stop
    const stopGroups = stopTimeData.reduce((acc, stop) => {
      if (!acc[stop.stopId]) {
        acc[stop.stopId] = {
          stopId: stop.stopId,
          stopName: stop.stopName || `Stop ${stop.stopId}`, // Fallback to stop ID if name is empty
          scheduledTimes: [],
          actualTimes: [],
          dates: [],
          percentInWindow: null
        };
      }
      
      const [date, time] = stop.scheduledTime.split(' ');
      const [hours, minutes] = time.split(':');
      const scheduledTimeValue = (parseInt(hours) * 60 + parseInt(minutes)) * 60 * 1000;
      
      const [actualDate, actualTime] = (stop.actualTime || stop.scheduledTime).split(' ');
      const [actualHours, actualMinutes] = actualTime.split(':');
      const actualTimeValue = (parseInt(actualHours) * 60 + parseInt(actualMinutes)) * 60 * 1000;
      
      acc[stop.stopId].scheduledTimes.push(scheduledTimeValue);
      acc[stop.stopId].actualTimes.push(actualTimeValue);
      acc[stop.stopId].dates.push(stop.tripDate);
      
      return acc;
    }, {});

    // Process distribution data
    const distributionData = proposedTimetables.proposedTimetables
      .filter(item =>
        item.routeNo === selectedRoute &&
        item.tripId &&
        item.tripId.split(',').includes(selectedTrip)
      )
      .sort((a, b) => {
        const [dateA, timeA] = a.scheduledTime.split(' ');
        const [dateB, timeB] = b.scheduledTime.split(' ');
        const [hoursA, minutesA, secondsA] = timeA.split(':');
        const [hoursB, minutesB, secondsB] = timeB.split(':');
        const timeValueA = (parseInt(hoursA) * 60 * 60) + (parseInt(minutesA) * 60) + parseInt(secondsA);
        const timeValueB = (parseInt(hoursB) * 60 * 60) + (parseInt(minutesB) * 60) + parseInt(secondsB);
        return timeValueA - timeValueB;
      });

    console.log('Distribution Data:', distributionData.map(item => ({
      stopId: item.stopId,
      stopName: item.stopName,
      scheduledTime: item.scheduledTime,
      stdDev: item.stdDev
    })));

    // Transform data for the chart
    const chartData = Object.values(stopGroups).map(stop => {
      const baseData = {
        stopName: stop.stopName,
        stopId: stop.stopId,
        scheduledTime: stop.scheduledTimes[0],
        actualTimes: stop.actualTimes,
        dates: stop.dates,
        percentInWindow: stop.percentInWindow
      };

      // Add distribution data if available
      const distributionStop = distributionData.find(d => 
        d.stopId === stop.stopId || // Try matching by ID first
        d.stopName === stop.stopName // Then try matching by name
      );
      console.log('Stop matching:', {
        stopName: stop.stopName,
        stopId: stop.stopId,
        foundMatch: !!distributionStop,
        distributionStopName: distributionStop?.stopName,
        distributionStopId: distributionStop?.stopId
      });
      if (distributionStop) {
        console.log('Found distribution stop:', {
          stopName: stop.stopName,
          distributionStop,
          stdDev: distributionStop.stdDev,
          scheduledTime: distributionStop.scheduledTime
        });
        const [date, time] = distributionStop.scheduledTime.split(' ');
        const [hours, minutes, seconds] = time.split(':');
        const baseTimeValue = (parseInt(hours) * 60 * 60) + (parseInt(minutes) * 60) + parseInt(seconds);
        
        // Calculate adjustments for each model
        const modelAdjustments = STATISTICAL_MODELS.map(model => {
          const stdDevMinutes = (distributionStop.stdDev || 0) / 60;
          const meanVarianceMinutes = (distributionStop.meanVariance || 0) / 60; // meanVariance is in seconds
          const targetPercentile = selectedOTR === 100 ? 0.001 : selectedOTR / 100;
          const adjustmentMinutes = model.calculateAdjustment(
            stdDevMinutes,
            targetPercentile,
            (model.id === 'normal' || model.id === 'empirical') ? (distributionStop.variances || []) : undefined
          );
          const totalAdjustmentMinutes = meanVarianceMinutes + adjustmentMinutes;
          console.log(`Model ${model.id} adjustment details:`, {
            modelId: model.id,
            stdDevMinutes,
            meanVarianceMinutes,
            targetPercentile,
            adjustmentMinutes,
            totalAdjustmentMinutes,
            baseTimeValue,
            adjustedTimeValue: baseTimeValue + (totalAdjustmentMinutes * 60),
            finalTime: (baseTimeValue + (totalAdjustmentMinutes * 60)) * 1000
          });
          return {
            modelId: model.id,
            adjustmentMinutes: totalAdjustmentMinutes
          };
        });

        // Add model times and calculate average
        const modelTimes = modelAdjustments.map(({ modelId, adjustmentMinutes }) => {
          const adjustedTimeValue = baseTimeValue + (adjustmentMinutes * 60);
          const finalTime = adjustedTimeValue * 1000; // Convert to milliseconds
          return {
            modelId,
            time: finalTime
          };
        });

        const averageModelTime = modelTimes.reduce((sum, { time }) => sum + time, 0) / modelTimes.length;

        // Custom logic for new line
        const getModelTime = (id) => {
          const found = modelTimes.find(m => m.modelId === id);
          return found ? found.time : null;
        };
        const empiricalTime = getModelTime('empirical');
        const lognormalTime = getModelTime('lognormal');
        const weibullTime = getModelTime('weibull');
        let customModelTime = averageModelTime;
        if (empiricalTime && lognormalTime && Math.abs(empiricalTime - lognormalTime) <= 60 * 1000) {
          customModelTime = lognormalTime;
        } else if (weibullTime && empiricalTime && (weibullTime - empiricalTime) > 2 * 60 * 1000) {
          customModelTime = null; // flag as delay zone
        }

        return {
          ...baseData,
          ...modelTimes.reduce((acc, { modelId, time }) => ({
            ...acc,
            [`${modelId}Time`]: time
          }), {}),
          averageModelTime,
          customModelTime,
          percentInWindow: stop.percentInWindow
        };
      }

      return baseData;
    });

    console.log('Final chart data:', chartData);
    return chartData.sort((a, b) => a.scheduledTime - b.scheduledTime);
  }, [reportData, proposedTimetables, selectedRoute, selectedTrip, selectedOTR, STATISTICAL_MODELS]);

  const chartData = processData || [];

  // Enforce monotonicity for recommendedTime
  let lastRecommended = null;
  for (let i = 0; i < chartData.length; i++) {
    if (chartData[i].customModelTime != null) {
      if (lastRecommended != null && chartData[i].customModelTime < lastRecommended) {
        chartData[i].customModelTime = lastRecommended;
      }
      lastRecommended = chartData[i].customModelTime;
    }
  }

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Select 
              value={selectedRoute} 
              onChange={value => { setSelectedRoute(value); setSelectedTrip('all'); }}
              style={{ width: '100%' }}
              placeholder="Select route"
            >
              <Option value="all">All Routes</Option>
              {routes.map(route => (
                <Option key={route} value={route}>Route {route}</Option>
              ))}
            </Select>
          </Col>
          <Col span={12}>
            {selectedRoute !== 'all' && (() => {
              const tripIds = [...new Set(
                reportData.parsedData
                  .filter(item => item.routeNo === selectedRoute)
                  .map(item => item.tripId)
              )].sort();
              
              return (
                <Select
                  value={selectedTrip}
                  onChange={setSelectedTrip}
                  style={{ width: '100%' }}
                  placeholder="Select trip"
                >
                  <Option value="all">All Trips</Option>
                  {tripIds.map(tripId => {
                    // Find the earliest stop for this trip
                    const stopsForTrip = reportData.parsedData.filter(item =>
                      item.routeNo === selectedRoute &&
                      item.tripId === tripId
                    );
                    const earliest = stopsForTrip.reduce((min, stop) => {
                      if (!min) return stop;
                      return stop.scheduledTime < min.scheduledTime ? stop : min;
                    }, null);
                    let startTime = '';
                    if (earliest && earliest.scheduledTime) {
                      const timePart = earliest.scheduledTime.split(' ')[1];
                      if (timePart) {
                        const [h, m] = timePart.split(':');
                        startTime = `${h}:${m}`;
                      }
                    }
                    return (
                      <Option key={tripId} value={tripId}>
                        Trip {tripId} ({startTime})
                      </Option>
                    );
                  })}
                </Select>
              );
            })()}
          </Col>
        </Row>

        <Divider style={{ margin: '16px 0' }} />

        <Row gutter={16}>
          <Col span={12}>
            <Space>
              <Switch 
                checked={showStopTimes}
                onChange={setShowStopTimes}
              />
              <Text>Show Stop Times</Text>
            </Space>
          </Col>
          <Col span={12}>
            <Space>
              <Switch 
                checked={showDistributions}
                onChange={setShowDistributions}
              />
              <Text>Show Distribution Models</Text>
            </Space>
          </Col>
        </Row>
      </div>
      
      {selectedRoute === 'all' || selectedTrip === 'all' ? (
        <Alert
          message="Select a Route and Trip"
          description={
            <AntParagraph>
              Please select a specific route and trip from the filters above to view the hybrid analysis.
              This will show you both the actual stop times and the statistical model distributions.
            </AntParagraph>
          }
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
      ) : !chartData || chartData.length === 0 ? (
        <Empty description="No data available for the selected route and trip" />
      ) : (
        <Card>
          <div style={{ height: 800 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 50, bottom: 100 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="stopName"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                />
                <YAxis
                  tickFormatter={value => {
                    const hours = Math.floor(value / (60 * 60 * 1000));
                    const minutes = Math.floor((value % (60 * 60 * 1000)) / (60 * 1000));
                    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                  }}
                  domain={[
                    dataMin => dataMin - 1000,
                    dataMax => dataMax + 1000
                  ]}
                  scale="time"
                  type="number"
                />
                <RechartsTooltip
                  formatter={(value, name) => {
                    // If value is a time in ms, format as HH:mm
                    if (typeof value === 'number' && value > 1000000) {
                      const date = new Date(value);
                      const hours = date.getUTCHours().toString().padStart(2, '0');
                      const minutes = date.getUTCMinutes().toString().padStart(2, '0');
                      return [`${hours}:${minutes}`, name];
                    }
                    return [value, name];
                  }}
                  labelFormatter={label => `Stop: ${label}`}
                  filterNull={true}
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      // Filter out actual time data
                      const filteredPayload = payload.filter(item => 
                        !item.dataKey.startsWith('actualTimes')
                      );
                      
                      return (
                        <div style={{ 
                          backgroundColor: 'white', 
                          padding: '10px', 
                          border: '1px solid #ccc',
                          borderRadius: '4px'
                        }}>
                          <p style={{ margin: '0 0 5px 0' }}>{`Stop: ${label}`}</p>
                          {filteredPayload.map((entry, index) => (
                            <p key={index} style={{ 
                              margin: '0',
                              color: entry.color,
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: '10px'
                            }}>
                              <span>{entry.name}:</span>
                              <span>{entry.value ? new Date(entry.value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A'}</span>
                            </p>
                          ))}
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend />
                
                {/* Scheduled Time Line */}
                <Line
                  type="monotone"
                  dataKey="scheduledTime"
                  name="Scheduled Time"
                  stroke="#8884d8"
                  strokeWidth={3}
                  dot={true}
                />

                {/* Average Model Time Line */}
                {showDistributions && (
                  <Line
                    type="monotone"
                    dataKey="averageModelTime"
                    name={`Average Model Time (${selectedOTR}% OTR)`}
                    stroke="#ff0000"
                    strokeWidth={3}
                    dot={true}
                  />
                )}

                {/* Individual Model Lines */}
                {showDistributions && STATISTICAL_MODELS.map((model, index) => (
                  <Line
                    key={model.id}
                    type="monotone"
                    dataKey={`${model.id}Time`}
                    name={`${model.name} (${selectedOTR}% OTR)`}
                    stroke={['#82ca9d', '#ffc658', '#ff8042', '#0088fe'][index % 4]}
                    strokeWidth={1}
                    dot={false}
                  />
                ))}

                {/* Recommended Time Line */}
                {showDistributions && (
                  <Line
                    type="monotone"
                    dataKey="customModelTime"
                    name="Recommended Time"
                    stroke="#800080"
                    strokeWidth={3}
                    dot={true}
                  />
                )}

                {/* Actual Stop Time Lines */}
                {showStopTimes && chartData[0].actualTimes.map((_, index) => (
                  <Line
                    key={`actual-${index}`}
                    type="monotone"
                    dataKey={`actualTimes[${index}]`}
                    name={index === 0 ? `Actual Time` : undefined}
                    stroke="#999999"
                    strokeWidth={1}
                    dot={true}
                    legendType={index === 0 ? 'line' : 'none'}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
          
          <div style={{ marginTop: 16 }}>
            <Alert
              message="Hybrid Analysis"
              description={
                <AntParagraph>
                  This chart combines actual stop times (gray lines) with statistical model distributions (colored lines).
                  The blue line shows the scheduled time, while the red line represents the average of all model adjustments.
                  Use the switches above to toggle the visibility of different data types.
                </AntParagraph>
              }
              type="info"
              showIcon
            />
          </div>
        </Card>
      )}
    </>
  );
};

export default HybridAnalysis; 